<!--*
 * Login form to access the application
 *
 * @author   pablo.barco
 * @date     2020-06-18
 *
 * @copyright 2020 Smartmatic Corp. All Rights Reserved.
 *
 * @license
 *
 * Confidential information of Smartmatic Labs. Not for disclosure or
 * distribution prior written consent. This software contains code, techniques
 * and know-how which is confidential and proprietary to Smartmatic.
 *
 * Use of this software is subject to the terms of an end user license
 * agreement.
-->
<template>
  <form id="loginForm" @submit="submitLogin">
  <div class="row">
      <div class="col--12">
        <smtt-contextual-box ref="userErrorBox" is-visible="false" :message-title="errorMessage"
                             variation="error" dismissable="true"></smtt-contextual-box>
      </div>
    </div>
    <div class="row">
      <div class="col--12">
        <smtt-input
          ref="usernameInput"
          :error-msg="usernameErrorMsg"
          required type="text"
          :label="$t('login.loginForm.usernameInput.label')"
          full-width="true"
          form="loginForm"
          @keyup.enter="submitLogin"
          @inputValueChange="usernameChanged"
        >
        </smtt-input>
      </div>
    </div>
    <div class="row">
      <div class="col--12">
        <smtt-input
          ref="passwordInput"
          :error-msg="passwordErrorMsg"
          type="password"
          required
          form="loginForm"
          full-width="true"
          :label="$t('login.loginForm.passwordInput.label')"
          @keyup.enter="submitLogin"
          @inputValueChange="passwordChanged"
        >
        </smtt-input>
      </div>
    </div>
    <div class="row">
      <div class="col--12">
        <button :disabled="loading" type="submit" class="btn btn--primary btn--solid btn--full-width justify-content-center">
          {{ $t('login.login.button.label') }}
        </button>
      </div>
    </div>
    <smtt-toast ref="statusErrorToast" id="errorToast"></smtt-toast>
    <recaptcha
      ref="recaptcha"
      v-if="hasRecaptchaEnabled"
      :sitekey="recaptchaSiteCode"
      @verify="login"
    />
  </form>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import store from "../../store/store";
import getEnv from '../../utils/env';
import Recaptcha from '../Recaptcha';
import { initializeFCM } from '../../utils/firebase';

export default {
  'name': 'LoginForm',
  'components': { Recaptcha },
  data() {
    return {
      'errorMessage': null,
      'username': null,
      'password': null,
      'usernameErrorMsg': this.$t('login.username.error.message'),
      'passwordErrorMsg': this.$t('login.password.error.message'),
      'recaptchaEnabled': false,
      'recaptchaSiteCode': null,
      'loading': false,
    }
  },
  computed: {
    ...mapState(['fcmToken']),
    hasRecaptchaEnabled() {
      return this.recaptchaEnabled && this.recaptchaSiteCode?.length > 0;
    }
  },
  'methods': {
    ...mapActions([
      'loginUser',
      'getSingleAssignmentByUser',
    ]),
    hasRole(roles) {
      let allowed = false
      for (let i = 0; i < roles.length; i++) {
        if (roles[i].role === 'SURVEYOR' || roles[i].role === 'SUPERVISOR' || roles[i].role === 'SurveyorNoAddRoom' || roles[i].role === 'OPERATOR') {
          allowed = true
          break
        }
      }
      return allowed
    },
    'login': async function (recaptcha_response) {
      // Wait for FCM token resolution before making API call
      await new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (this.fcmToken.status !== 'loading') {
            clearInterval(intervalId);
            resolve();
          }
        }, 500);
      });

      try {
        const response = await this.loginUser({
          'username': this.username,
          'password': this.password,
          'recaptcha_response': recaptcha_response ?? null,
          'fcmToken': this.fcmToken.token,
        });

        if (!response.success) {
          this.$refs.userErrorBox.isVisible = true
          this.errorMessage = this.$t(response.message.code, response.message.params)
        } else {
          if (store.getters.HasAdminPermission(response.userData.permissions)) {
            // Let's go to Admin's Page
            window.location.replace(store.getters.SystemUlrBase() + '/admin/')
            this.$store.commit('setIsLoginExternal', { 'isLoginExternal': false });
          } else if (this.hasRole(response.userData.roles) || store.getters.HasDataColPermission(response.userData.permissions)) {
            // If user has only 1 site, navigate to that site
            this.getSingleAssignmentByUser().then((response) => {
              if (!response.success) {
                this.$router.replace({ 'name': 'Welcome' });
              } else {
                if (response.data.isSingleAssignment) {
                  this.$router.replace({ 'name': 'SiteDetailsScreen', 'params': { 'assignmentId': response.data.assignmentId } });
                } else {
                  this.$router.replace({ 'name': 'Welcome' });
                }
              }
            }).catch(() => {
              // Let's go to DataCol Page
              this.$router.replace({ 'name': 'Welcome' })
            });

            this.$store.commit('setIsLoginExternal', { 'isLoginExternal': false });
          } else {
            // We are not going anywhere because the user does not have permission to do it.
            this.$refs.userErrorBox.isVisible = true
            this.errorMessage = this.$t('login.error.permission.message')
          }
        }
      } catch(error) {
        const toastElement = this.$refs.statusErrorToast;
        toastElement.error({
          'message': error.response?.message != null ? this.$t(error.response.message.code, error.response.message.params) : this.$t('error.default.connection.message'),
          'header': this.$t('error.default.connection.title'),
          'duration': 5000
        });
      } finally{
        this.loading = false;
      }
    },
    'submitLogin': function (e) {
      e.preventDefault()
      this.loading = true;

      // Trigger FCM initialization on user gesture
      initializeFCM();

      if (this.validateForm()) {
        if (this.hasRecaptchaEnabled) {
          this.$refs.recaptcha.execute();
        } else {
          this.login();
        }
      }
    },
    'validateForm': function () {
      const usernameIsValid = this.$refs.usernameInput.checkValidity()
      const passwordIsValid = this.$refs.passwordInput.checkValidity()
      this.$refs.usernameInput.isInvalid = !usernameIsValid
      this.$refs.passwordInput.isInvalid = !passwordIsValid
      return usernameIsValid && passwordIsValid
    },
    'passwordChanged': function (e) {
      this.password = e.detail.value
    },
    'usernameChanged': function (e) {
      this.username = e.detail.value
    }
  },
  created() {
    this.recaptchaEnabled = getEnv('VUE_APP_RECAPTCHA_ENABLED')?.toLowerCase() === 'true';
    this.recaptchaSiteCode = getEnv('VUE_APP_RECAPTCHA_SITE_KEY');
  },
}
</script>

<style scoped>

</style>
